<template>
  <div class="personalization-container">
    <simple-snackbar
      :open.sync="glsSnackbar.open"
      :message-prop="glsSnackbar.message"
      color="success"
    />
    <v-expansion-panel :expand="true" :value="panel">
      <v-expansion-panel-content
        v-for="(item, index) in order"
        :key="index"
        ref="expansionPanel"
        class="panel-header"
      >
        <template v-slot:header>
          <div>{{ $t('ownerData') }} {{ item.name }} #{{ index + 1 }}</div>
        </template>

        <v-card>
          <div class="description-container">
            <p style="margin-top: 2.5rem">{{ $t('requiredFieldsDescription') }}</p>
          </div>
          <div v-if="fields.length" class="form-container">
            <v-layout column>
              <v-flex v-for="field in fields" :key="field.key" class="pb-16">
                <template v-if="shouldRenderField(index, field)">
                  <component
                    :is="field.component"
                    v-model="buyer.personalization[index][field.key]"
                    :validator="vs && vs.buyer.personalization.$each[index][field.key]"
                    v-bind="field"
                    :min="getFieldMin(index, field)"
                    :error-messages="field.errorMessages"
                    @change="handleFieldChange(index, field, $event)"
                  />
                  <div
                    v-if="shouldShowGlsMap(index, field)"
                    :key="getGlsMapKey(index)"
                    class="gls-map-container"
                  >
                    <gls-dpm
                      :id="getGlsMapId(index)"
                      country="hr"
                      language="hr"
                      filter-type="parcel-locker"
                    />
                  </div>
                  <div v-if="shouldShowGlsSelection(index, field)" class="gls-selection-summary">
                    <div class="gls-selection-text">
                      Odabrani GLS paketomat: {{ getGlsPaketomatValue(index) }}
                    </div>
                    <button type="button" class="gls-selection-button" @click="reopenGlsMap(index)">
                      Promijeni paketomat
                    </button>
                  </div>
                </template>
              </v-flex>
            </v-layout>
          </div>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

const GLS_WIDGET_SCRIPT_ID = 'gls-dpm-script'
const GLS_WIDGET_SCRIPT_URL = 'https://map.gls-hungary.com/widget/gls-dpm.js'
const GLS_ORDER_ITEM_IDS = [18723, 18724]
const MIN_AGE_ORDER_ITEM_IDS = [17475, 18724, 18722]

export default defineComponent({
  name: 'sport-organization-ticket-personalization',
  components: {
    SimpleSnackbar,
  },
  props: {
    order: {
      type: Array,
      default: () => [],
    },
    schema: {
      type: Object,
      default: () => null,
    },
    vs: {
      type: Object,
      default: () => {},
    },
    buyer: {
      type: Object,
      default: () => {},
    },
    personalizationErrors: {
      type: Object,
      required: true,
    },
    organizationId: {
      type: Number,
      default: null,
    },
    isMembershipPurchase: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fields: [],
      panel: [],
      glsMapListenerCleanup: {},
      glsMapVisible: {},
      glsSnackbar: {
        open: false,
        message: '',
      },
    }
  },
  computed: {
    shouldUseGlsPaketomatFlow() {
      return this.organizationId === 17 && this.isMembershipPurchase
    },
  },
  validations() {
    return {
      buyer: {
        personalization: {
          $each: {
            ...this.schema.validationSchema,
          },
        },
      },
    }
  },
  watch: {
    personalizationErrors: {
      handler(validation) {
        if (validation.error) {
          this.order.forEach(() => this.panel.push(1))
        } else {
          this.panel = []
        }
      },
      deep: true,
    },
    'buyer.personalization': {
      handler(personalization) {
        if (!this.shouldUseGlsPaketomatFlow || !Array.isArray(personalization)) {
          return
        }

        personalization.forEach((_, index) => {
          if (this.shouldUseGlsForOrderIndex(index) && this.isGlsPickupSelected(index)) {
            this.mountGlsMap(index)
          }
        })
      },
      deep: true,
    },
  },
  methods: {
    getFieldMin(index, field) {
      if (field.key === 'Datum rođenja') {
        const orderItem = this.order && this.order[index]
        if (orderItem && MIN_AGE_ORDER_ITEM_IDS.includes(Number(orderItem.id))) {
          const d = new Date()
          d.setFullYear(d.getFullYear() - 18)

          return d.toISOString().split('T')[0]
        }
      }

      return field && field.validations ? field.validations.minDate : undefined
    },
    async ensureGlsWidgetScript() {
      if (!this.shouldUseGlsPaketomatFlow || typeof window === 'undefined') {
        return
      }

      if (window.__glsWidgetScriptPromise) {
        return window.__glsWidgetScriptPromise
      }

      const existingScript = document.getElementById(GLS_WIDGET_SCRIPT_ID)
      if (existingScript) {
        window.__glsWidgetScriptPromise =
          window.__glsWidgetScriptPromise ||
          new Promise(resolve => {
            if (existingScript.dataset.loaded === 'true') {
              resolve()

              return
            }

            existingScript.addEventListener('load', () => resolve(), { once: true })
          })

        return window.__glsWidgetScriptPromise
      }

      window.__glsWidgetScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.id = GLS_WIDGET_SCRIPT_ID
        script.type = 'module'
        script.src = GLS_WIDGET_SCRIPT_URL
        script.onload = () => {
          script.dataset.loaded = 'true'
          resolve()
        }
        script.onerror = error => reject(error)
        document.head.appendChild(script)
      })

      return window.__glsWidgetScriptPromise
    },
    initPersonalizationForm() {
      this.buyer.personalization = []
      const ordersLength = this.order.length
      for (let i = 0; i < ordersLength; i++) {
        const item = { paymentMethod: 'CreditCard' }
        this.fields.forEach(field => {
          item[field.key] = ''
        })
        this.buyer.personalization.push(item)
      }
    },
    fieldOrder(fields) {
      let attendeeFields = [...fields]
      attendeeFields.forEach(
        function (item, i) {
          if (item.isPrivate && !this.shouldAllowPrivateField(item)) {
            attendeeFields.splice(i, 1)
          }
          if (item.component === 'avatar-field') {
            attendeeFields.splice(i, 1)
            attendeeFields.unshift(item)
          }
        }.bind(this),
      )

      return attendeeFields.map(field => {
        return {
          ...field,
          label: field.label + (field.validations.required ? '*' : ''),
        }
      })
    },
    shouldAllowPrivateField(field) {
      return this.shouldUseGlsPaketomatFlow && field.key === 'Način preuzimanja'
    },
    handleFieldChange(index, field, value) {
      if (
        !this.shouldUseGlsPaketomatFlow ||
        !this.shouldUseGlsForOrderIndex(index) ||
        field.key !== 'Način preuzimanja'
      ) {
        return
      }

      if (!this.isGlsOption(value)) {
        this.setGlsPaketomatValue(index, '')
        this.setGlsMapVisibility(index, false)

        return
      }

      this.setGlsMapVisibility(index, true)
      this.$nextTick(() => {
        this.mountGlsMap(index)
      })
    },
    shouldShowGlsMap(index, field) {
      return (
        this.shouldUseGlsPaketomatFlow &&
        this.shouldUseGlsForOrderIndex(index) &&
        field.key === 'Način preuzimanja' &&
        this.isGlsPickupSelected(index) &&
        this.isGlsMapVisible(index)
      )
    },
    shouldShowGlsSelection(index, field) {
      return (
        this.shouldUseGlsPaketomatFlow &&
        this.shouldUseGlsForOrderIndex(index) &&
        field.key === 'Način preuzimanja' &&
        this.isGlsPickupSelected(index) &&
        !!this.getGlsPaketomatValue(index) &&
        !this.isGlsMapVisible(index)
      )
    },
    shouldUseGlsForOrderIndex(index) {
      const orderItem = this.order && this.order[index]

      return !!(orderItem && GLS_ORDER_ITEM_IDS.includes(Number(orderItem.id)))
    },
    shouldRenderField(index, field) {
      if (field.key !== 'Način preuzimanja') {
        return true
      }

      return this.shouldUseGlsPaketomatFlow && this.shouldUseGlsForOrderIndex(index)
    },
    isGlsPickupSelected(index) {
      const personalization =
        this.buyer && this.buyer.personalization && this.buyer.personalization[index]

      return this.isGlsOption(personalization && personalization['Način preuzimanja'])
    },
    isGlsOption(value) {
      return (
        String(value || '')
          .trim()
          .toLowerCase() === 'gls paketomat'
      )
    },
    getGlsMapId(index) {
      return `gls-paketomat-map-${index}`
    },
    getGlsMapKey(index) {
      const personalization =
        this.buyer && this.buyer.personalization && this.buyer.personalization[index]
      const currentValue = (personalization && personalization['Način preuzimanja']) || ''
      const isVisible = this.isGlsMapVisible(index) ? 'open' : 'closed'

      return `${index}-${currentValue}-${isVisible}`
    },
    isGlsMapVisible(index) {
      return !!this.glsMapVisible[index]
    },
    setGlsMapVisibility(index, isVisible) {
      this.$set(this.glsMapVisible, index, isVisible)
    },
    reopenGlsMap(index) {
      if (!this.shouldUseGlsForOrderIndex(index)) {
        return
      }

      this.setGlsMapVisibility(index, true)
      this.$nextTick(() => {
        this.mountGlsMap(index)
      })
    },
    async mountGlsMap(index) {
      if (
        !this.shouldUseGlsPaketomatFlow ||
        !this.shouldUseGlsForOrderIndex(index) ||
        !this.isGlsPickupSelected(index)
      ) {
        return
      }

      try {
        await this.ensureGlsWidgetScript()
      } catch (error) {
        return
      }

      this.$nextTick(() => {
        const mapElement = document.getElementById(this.getGlsMapId(index))
        if (!mapElement || mapElement.dataset.listenerAttached === 'true') {
          return
        }

        const handleChange = event => {
          const selectedPoint = event.detail || null
          const formattedValue = this.formatGlsPaketomatValue(selectedPoint)

          this.setGlsPaketomatValue(index, formattedValue)

          if (formattedValue) {
            this.setGlsMapVisibility(index, false)
            this.openGlsSnackbar('GLS paketomat je uspješno odabran.')
          }
        }

        mapElement.addEventListener('change', handleChange)
        mapElement.dataset.listenerAttached = 'true'
        this.glsMapListenerCleanup[index] = () => {
          mapElement.removeEventListener('change', handleChange)
          delete mapElement.dataset.listenerAttached
        }
      })
    },
    setGlsPaketomatValue(index, value) {
      if (!this.buyer.personalization[index]) {
        return
      }

      this.buyer.personalization[index]['GLS paketomat'] = value
    },
    getGlsPaketomatValue(index) {
      const personalization =
        this.buyer && this.buyer.personalization && this.buyer.personalization[index]

      return (personalization && personalization['GLS paketomat']) || ''
    },
    openGlsSnackbar(message) {
      this.glsSnackbar.message = message
      this.glsSnackbar.open = false

      this.$nextTick(() => {
        this.glsSnackbar.open = true
      })
    },
    formatGlsPaketomatValue(selectedPoint) {
      if (!selectedPoint || (!selectedPoint.id && !selectedPoint.name)) {
        return ''
      }

      const contact = selectedPoint.contact || {}
      const locationParts = [
        selectedPoint.name,
        selectedPoint.id,
        contact.address,
        [contact.postalCode, contact.city].filter(Boolean).join(' '),
      ].filter(Boolean)

      return locationParts.join(', ')
    },
  },
  async mounted() {
    this.fields = this.fieldOrder(this.schema.attendeeFields)
    this.initPersonalizationForm()

    if (this.shouldUseGlsPaketomatFlow) {
      await this.ensureGlsWidgetScript()
    }

    // Open all panels by default
    this.order.forEach(() => this.panel.push(1))
  },
  beforeUnmount() {
    Object.values(this.glsMapListenerCleanup).forEach(cleanup => cleanup && cleanup())
  },
  beforeDestroy() {
    Object.values(this.glsMapListenerCleanup).forEach(cleanup => cleanup && cleanup())
  },
})
</script>
<style scoped>
.personalization-container {
  display: flex;
  flex-direction: column;
  padding: 32px 24px 40px 24px;
  gap: 2.5rem;
}

.panel-header {
  background-color: #e6f3ff !important;
}

.description-container {
  padding-top: 2px;
  margin-left: 10px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-left: 10px;
  margin-right: 10px;
  padding-top: 10px;
}

.gls-map-container {
  min-height: 600px;
  margin-top: 1rem;
}

.gls-map-container gls-dpm {
  display: block;
  height: 600px;
}

.gls-selection-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f4f9ff;
  border: 1px solid #b9d8f7;
  border-radius: 8px;
}

.gls-selection-text {
  color: #1f2d3d;
  line-height: 1.5;
  word-break: break-word;
}

.gls-selection-button {
  border: 0;
  background: transparent;
  color: #0054a5;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

@media screen and (max-width: 640px) {
  .gls-selection-summary {
    flex-direction: column;
  }
}
</style>
